import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    Callback,
    Callback1,
    CompRef,
    IdMap,
    IdMapValue,
    ImageGenerationServerlessResponseWithIdMap,
    ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    ImageInfo,
    ImagesInfoMap,
    UserRequestInfo,
    ImagesInfoMapFromServer,
    UploadResponse,
    FileDescriptorImage,
    MediaBatchContext,
    ExecutionIdToMappedId,
    FileMediaType as FileMediaTypeType
} from '@wix/document-services-types'
import {FileMediaType} from '../../../constants/dsTypesConstants'
import {reportBIError} from '../aiExtensionContent'
import {ERRORS, LOGS} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'
import {handleAiError} from '../aiBuilderUtils'
import {createMediaMap} from '../utils'
import type {DataExtensionAPI} from '../../data'
import {isEmpty, mapValues} from 'lodash'
import type {ImagesRequestBody} from './requests'
import {STATIC_MEDIA_URL} from '@wix/image-kit'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import {
    type UploadConfig,
    uploadFilesWithHandlers,
    uploadFilesWithHandlersSync,
    uploadSingleFileWithHandler
} from '../fileUploadHandler'

import {
    EventHandlerExtApi,
    Handler,
    HandlerResult,
    HandlerStatus,
    PayloadData,
    PayloadStatus
} from '../eventHandlerExtension'
import type {LoggerExtAPI} from '../../logger'

interface ValidationResult {
    isValid: boolean
    reason: string
}

export interface ImageContext extends MediaBatchContext<ImageInfo> {
    errorType?: string
}

export interface ImageGenerationParams {
    userRequestInfo?: UserRequestInfo
    onSuccess?: Callback
    onError?: Callback1<any>
    caller: string
    errorType: string
}

const injectImage = (createExtArgs: CreateExtArgs, idMapValue: IdMapValue, imageInfo: ImageInfo | null) => {
    const {dataModel} = createExtArgs.extensionAPI as DataModelExtensionAPI
    const {data} = createExtArgs.extensionAPI as DataExtensionAPI
    const {pointer, prop, path, nestedDataPath} = idMapValue ?? {}
    if (!pointer) return
    let imageData
    if (!imageInfo) {
        imageData = null
    } else {
        imageData = dataModel.createDataItemByType('Builder.Image', {
            uri: imageInfo.imageUri,
            ...(imageInfo.width && imageInfo.height ? {width: imageInfo.width, height: imageInfo.height} : {}),
            ...(imageInfo.name ? {name: imageInfo.name} : {}),
            visualEffects: null
        })
    }

    data.injectData({pointer, path, nestedDataPath, partialNewData: {[prop]: imageData}})
}

export const createImageUploadConfig = (logger: any, caller?: string): UploadConfig<ImageInfo | null> => ({
    extractData: (output: UploadResponse): FileDescriptorImage | undefined => output?.file?.media?.image?.image,
    transformData: (imageData: FileDescriptorImage) => ({
        imageUri: imageData.id!,
        width: imageData.width,
        height: imageData.height,
        name: imageData.filename!
    }),
    injectItem: injectImage,
    errorMessage: 'Image upload failed',
    timeOutInMS: 60000,
    onItemUploadStart: (url: string) => {
        logger.interactionStarted(LOGS.IMAGES.UPLOAD, {tags: {caller}, extras: {url}})
    },
    onItemUploadComplete: (url: string) => {
        logger.interactionEnded(LOGS.IMAGES.UPLOAD, {tags: {caller}, extras: {url}})
    }
})

export const createLogoUploadConfig = (logger: any, caller?: string): UploadConfig<ImageInfo | null> => ({
    ...createImageUploadConfig(logger, caller),
    errorMessage: 'Logo upload failed',
    onItemUploadStart: (url: string) => {
        logger.interactionStarted(LOGS.LOGOS.UPLOAD, {tags: {caller}, extras: {url}})
    },
    onItemUploadComplete: (url: string) => {
        logger.interactionEnded(LOGS.LOGOS.UPLOAD, {tags: {caller}, extras: {url}})
    }
})

const validateExecutionIdToMappedId = (
    executionIdToMappedId: Record<string, string>,
    idMap: IdMap
): ValidationResult => {
    const missingExecutionIds = Object.keys(idMap).filter(img => !Object.values(executionIdToMappedId).includes(img))
    return {
        isValid: missingExecutionIds.length === 0,
        reason:
            missingExecutionIds.length === 0
                ? ''
                : `Failed to generate ${
                      Object.keys(idMap).length - Object.keys(executionIdToMappedId).length
                  } urls for ${missingExecutionIds}`
    }
}

const validateResult = (images: ImagesInfoMapFromServer, idMap: IdMap): ValidationResult => {
    const missingImagesUrl = Object.keys(idMap).filter(img => !images.hasOwnProperty(img) || images[img] === null)
    return {
        isValid: missingImagesUrl.length === 0,
        reason:
            missingImagesUrl.length === 0
                ? ''
                : `Failed to generate ${
                      Object.keys(idMap).length - Object.keys(images).length
                  } urls for ${missingImagesUrl}`
    }
}

const restoreOriginalImages = (createExtArgs: CreateExtArgs, idMap: IdMap, oldImagesInfo: ImagesInfoMap) => {
    Object.entries(idMap).forEach(([mappedId, idMapValue]) => {
        injectImage(createExtArgs, idMapValue, oldImagesInfo[mappedId] ?? null)
    })
    createExtArgs.dal.commitTransaction()
}

export const uploadImages = async (
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    imagesInfo: ImagesInfoMap,
    idMap: IdMap,
    oldImagesInfo: ImagesInfoMap,
    failures: string[] = [],
    onSuccess?: Callback1<any>,
    onError?: Callback1<any>,
    caller?: string,
    config?: UploadConfig<ImageInfo | null>,
    signal?: AbortSignal
) => {
    if (signal?.aborted) {
        const {logger} = createExtArgs.coreConfig
        logger.breadcrumb('uploadImages aborted', {tags: {caller}, extras: {stage: 'entry'}})
        restoreOriginalImages(createExtArgs, idMap, oldImagesInfo)
        return
    }

    const context: ImageContext = {
        createExtArgs,
        idMap,
        originalData: oldImagesInfo,
        onSuccess,
        onError,
        failures,
        totalItems: Object.keys(imagesInfo).length,
        serverFacade,
        processedItems: 0,
        mediaType: FileMediaType.IMAGE as FileMediaTypeType,
        caller
    }

    const {coreConfig} = createExtArgs
    const {experimentInstance} = coreConfig
    const {logger} = createExtArgs.coreConfig

    const imageUploadConfig = config ?? createImageUploadConfig(logger, caller)
    const items = mapValues(imagesInfo, ({imageUri}) => imageUri)
    if (experimentInstance.isOpen('dm_uploadSync')) {
        await uploadFilesWithHandlersSync(context, imageUploadConfig, items)
    } else {
        await uploadFilesWithHandlers(context, imageUploadConfig, items)
    }
}

export const injectImages = (
    createExtArgs: CreateExtArgs,
    {data: imagesInfo, idMap}: ImageGenerationServerlessResponseWithIdMap
) => {
    Object.entries(imagesInfo).forEach(([id, {imageUri, width, height}]) => {
        injectImage(createExtArgs, idMap[id], {imageUri, width, height})
    })
    createExtArgs.dal.commitTransaction()
}

export const uploadAndInjectImages = async (
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    {data: imagesInfo, idMap, oldImagesInfo}: ImageGenerationServerlessResponseWithIdMapAndOldUrlMap,
    failures: string[] = [],
    onSuccess?: Callback1<any>,
    onError?: Callback1<any>,
    caller?: string,
    signal?: AbortSignal
): Promise<void> => {
    if (!caller) {
        caller = 'uploadAndInjectImages'
    }
    const {logger} = createExtArgs.coreConfig

    if (signal?.aborted) {
        logger.breadcrumb('uploadAndInjectImages aborted', {tags: {caller}, extras: {stage: 'before-inject'}})
        return
    }

    injectImages(createExtArgs, {data: imagesInfo, idMap})

    const onSuccessWithLog = (arg: any) => {
        if (signal?.aborted) {
            logger.breadcrumb('uploadAndInjectImages aborted', {tags: {caller}, extras: {stage: 'before-onSuccess'}})
            restoreOriginalImages(createExtArgs, idMap, oldImagesInfo)
            return
        }
        logger.interactionEnded(caller, {tags: {caller}})
        onSuccess?.(arg)
    }
    const onErrorWithLog = (error: any) => {
        if (signal?.aborted) {
            logger.breadcrumb('uploadAndInjectImages aborted', {tags: {caller}, extras: {stage: 'before-onError'}})
            restoreOriginalImages(createExtArgs, idMap, oldImagesInfo)
            return
        }
        const reportableError = new ReportableError({
            errorType: ERRORS.IMAGES.UPLOAD,
            message: 'Images upload failed',
            extras: {
                originalError: error
            }
        })
        logger.captureError(reportableError, {tags: {caller}})
        onError?.(error)
    }

    await uploadImages(
        createExtArgs,
        serverFacade,
        imagesInfo,
        idMap,
        oldImagesInfo,
        failures,
        onSuccessWithLog,
        onErrorWithLog,
        caller,
        undefined,
        signal
    )
}

export const processGeneratedImages = async (
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    imagesInfoFromServer: ImagesInfoMapFromServer,
    idMap: IdMap,
    oldImagesInfo: ImagesInfoMap,
    onSuccess: Callback1<any> | undefined,
    onError: Callback1<any> | undefined,
    caller: string,
    signal?: AbortSignal
) => {
    const {logger} = createExtArgs.extensionAPI as LoggerExtAPI

    if (signal?.aborted) {
        logger.breadcrumb('processGeneratedImages aborted', {tags: {caller}, extras: {stage: 'entry'}})
        return
    }

    const validation = validateResult(imagesInfoFromServer, idMap)
    const failures: string[] = []

    if (!validation.isValid) {
        reportBIError(
            logger,
            ERRORS.IMAGES.GENERATION_VALIDATION_ERROR,
            validation.reason,
            imagesInfoFromServer,
            validation,
            caller
        )
        failures.push(validation.reason)
    }
    const imagesInfo: ImagesInfoMap = Object.fromEntries(
        Object.entries(imagesInfoFromServer).filter(([, info]) => info !== null)
    ) as ImagesInfoMap

    if (isEmpty(imagesInfo)) {
        const reportableError = new ReportableError({
            errorType: ERRORS.IMAGES.GENERATION,
            message: 'Images generation returned an empty object'
        })
        logger.captureError(reportableError, {tags: {caller}})
        onError?.({errors: ['No images generated']})
        return
    }

    await uploadAndInjectImages(
        createExtArgs,
        serverFacade,
        {data: imagesInfo, idMap, oldImagesInfo},
        failures,
        onSuccess,
        onError,
        caller,
        signal
    )
}

export const createStaticUrl = (uri: string, extras?: Record<string, any>) => {
    if (uri.startsWith('http')) {
        throw new ReportableError({
            errorType: 'ImageUriError',
            message: 'Image uri should be image id and not a full url',
            extras
        })
    }
    return STATIC_MEDIA_URL + uri
}

interface MediaHandlerConfig {
    outputUrlKey: string
    generationLog: string
    createUploadConfig(logger: any, caller?: string): UploadConfig<ImageInfo | null>
    restoreOnError: boolean
    missingUrlMessage: string
}

export const createMediaHandler = (context: ImageContext, mappedId: string, config: MediaHandlerConfig): Handler => {
    const handleError = (errorMessage: string): HandlerResult => {
        if (config.restoreOnError) {
            injectImage(context.createExtArgs, context.idMap[mappedId], context.originalData[mappedId])
        }
        context.failures.push(errorMessage)
        context.processedItems++
        context.createExtArgs.dal.commitTransaction()
        if (context.processedItems === context.totalItems) {
            context.onError?.({errors: context.failures})
        }
        return {status: HandlerStatus.FAILED, reason: errorMessage}
    }

    return async (data: PayloadData): Promise<HandlerResult> => {
        const {status, output} = data ?? {}
        const {errorType} = context
        const {experimentInstance} = context.createExtArgs.coreConfig
        const {logger} = context.createExtArgs.extensionAPI as LoggerExtAPI

        try {
            switch (status) {
                case PayloadStatus.FAILED: {
                    const reason = output?.reason ?? errorType ?? ''
                    logger.captureError(
                        new ReportableError({
                            errorType: errorType ?? 'MediaHandlerError',
                            message: reason
                        }),
                        {
                            tags: {caller: context.caller},
                            extras: {mappedId, output, idMapValue: context.idMap[mappedId]}
                        }
                    )
                    return handleError(reason)
                }

                case PayloadStatus.COMPLETED: {
                    const url = output?.[config.outputUrlKey]
                    if (!url) return handleError(config.missingUrlMessage)
                    logger.interactionEnded(config.generationLog, {
                        tags: {caller: context.caller},
                        extras: {context: context.idMap[mappedId]}
                    })
                    injectImage(context.createExtArgs, context.idMap[mappedId], {imageUri: url})
                    context.createExtArgs.dal.commitTransaction()

                    const uploadConfig = config.createUploadConfig(logger, context.caller)
                    if (experimentInstance.isOpen('dm_uploadSync')) {
                        await uploadFilesWithHandlersSync(context, uploadConfig, {[mappedId]: url})
                    } else {
                        await uploadSingleFileWithHandler(context, uploadConfig, mappedId, url)
                    }
                    return {status: HandlerStatus.COMPLETED}
                }

                case PayloadStatus.RUNNING:
                    return {status: HandlerStatus.RUNNING}
            }
        } catch (error: any) {
            return handleError(error.message)
        }
    }
}

const createImageHandler = (context: ImageContext, mappedId: string): Handler =>
    createMediaHandler(context, mappedId, {
        outputUrlKey: 'image_url',
        generationLog: LOGS.IMAGES.GENERATION,
        createUploadConfig: createImageUploadConfig,
        restoreOnError: true,
        missingUrlMessage: 'Image url is missing'
    })

interface WorkflowLogs {
    workflow: string
    generation: string
}

interface WorkflowErrors {
    validation: string
    generation: string
    emptyMessage: string
    noItemsMessage: string
}

export const executeWorkflow = async (
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    idMap: IdMap,
    originalData: Record<string, any>,
    requestBody: any,
    fetcher: (serverFacade: any, requestBody: any) => Promise<ExecutionIdToMappedId>,
    {
        caller,
        errorType,
        onSuccess,
        onError
    }: Pick<ImageGenerationParams, 'caller' | 'errorType' | 'onSuccess' | 'onError'>,
    logs: WorkflowLogs,
    errors: WorkflowErrors,
    createHandler: (context: ImageContext, mappedId: string) => Handler
): Promise<void> => {
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI
    const pointers = Object.values(idMap).map(v => v.pointer as CompRef)

    const onSuccessWithLog = () => {
        logger.interactionEnded(caller, {tags: {caller}})
        onSuccess?.()
    }
    const onErrorWithLog = (error: any) => {
        logger.captureError(
            new ReportableError({errorType, message: 'AI FLOW FAILED', extras: {originalError: error}}),
            {tags: {caller}}
        )
        onError?.(error)
    }

    logger.interactionStarted(caller, {tags: {caller}})

    let executionIdToMappedId: Record<string, string>
    try {
        logger.interactionStarted(logs.workflow, {tags: {caller, pointers}})
        executionIdToMappedId = await fetcher(serverFacade, requestBody)
        logger.interactionEnded(logs.workflow, {tags: {caller, pointers}})

        const validation = validateExecutionIdToMappedId(executionIdToMappedId, idMap)
        const failures: string[] = []

        if (!validation.isValid) {
            reportBIError(logger, errors.validation, validation.reason, executionIdToMappedId, validation, caller)

            if (isEmpty(executionIdToMappedId)) {
                logger.captureError(new ReportableError({errorType: errors.generation, message: errors.emptyMessage}), {
                    tags: {caller}
                })
                onError?.({errors: [errors.noItemsMessage]})
                return
            }
            failures.push(validation.reason)
        }

        const context: ImageContext = {
            createExtArgs,
            idMap,
            originalData,
            onSuccess: onSuccessWithLog,
            onError: onErrorWithLog,
            totalItems: Object.keys(executionIdToMappedId).length,
            serverFacade,
            mediaType: FileMediaType.IMAGE as FileMediaTypeType,
            failures,
            processedItems: 0,
            caller,
            errorType
        }

        Object.entries(executionIdToMappedId).forEach(([executionId, mappedId]) => {
            logger.interactionStarted(logs.generation, {tags: {caller}, extras: {context: context.idMap[mappedId]}})
            const handler = createHandler(context, mappedId)
            ;(extensionAPI as EventHandlerExtApi).eventHandler.registerExecutionIdToHandler(
                executionId,
                handler,
                caller
            )
        })
    } catch (error: any) {
        handleAiError(error, errorType, logger, onError, {caller})
    }
}

export const generateAndInjectImagesWorkflow = async <TParams>(
    createExtArgs: CreateExtArgs,
    serverFacade: any,
    componentPointers: CompRef[],
    buildParams: (key: string, url: string, width: number, height: number) => TParams,
    fetcher: (serverFacade: any, requestBody: ImagesRequestBody<TParams>) => Promise<ExecutionIdToMappedId>,
    {userRequestInfo, onSuccess, onError, caller, errorType}: ImageGenerationParams
): Promise<void> => {
    const {extensionAPI} = createExtArgs
    const {idMap, originalData, mediaMeasurements} = createMediaMap(extensionAPI, componentPointers, 'image', caller)
    const oldImageInfoMap: ImagesInfoMap = mapValues(idMap, (idMapValue, mappedId) => ({
        imageUri: originalData[mappedId]?.uri,
        width: originalData[mappedId]?.width,
        height: originalData[mappedId]?.height
    }))
    const images: Record<string, TParams> = mapValues(oldImageInfoMap, ({imageUri}, key) => {
        const {width, height} = mediaMeasurements[key]
        return buildParams(key, createStaticUrl(imageUri, {componentPointer: idMap[key].pointer}), width, height)
    })

    return executeWorkflow(
        createExtArgs,
        serverFacade,
        idMap,
        originalData,
        {images, userRequestInfo},
        fetcher,
        {caller, errorType, onSuccess, onError},
        {workflow: LOGS.IMAGES.WORKFLOW, generation: LOGS.IMAGES.GENERATION},
        {
            validation: ERRORS.IMAGES.GENERATION_VALIDATION_ERROR,
            generation: ERRORS.IMAGES.GENERATION,
            emptyMessage: 'Images generation returned an empty object',
            noItemsMessage: 'No images generated'
        },
        createImageHandler
    )
}
